'use client';

import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import {
  FetchOrderWorkProofReportResponseBody,
  OrderWorkProofInspectionSummary,
} from 'bff';
import { useTranslations } from 'i11n';
import React, { useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Spinner,
} from 'ui';
import { IFrameLoader } from './IFrameLoader';

// The report is produced asynchronously, so the dialog polls OUR endpoint while
// it is being generated. 3s is short enough to feel immediate at a ~12s p99.
const GENERATING_POLL_MS = 3000;

export const buildInspectionReportQueryKey = (inspectionId: string) => [
  `inspection-report-${inspectionId}`,
];

const alertIcon = <IconAlertTriangle className='size-6 text-amber-500' />;

const Message = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className='flex min-h-[12rem] flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground'>
    {icon}
    <p className='max-w-sm'>{children}</p>
  </div>
);

/**
 * Inspection report viewer, shared by insurer-app and customer-app. It only ever
 * shows OUR stored PDF: Autoinspector's own UI is never embedded here.
 *
 * The fetcher arrives as a prop (same pattern as `OrderWorkProofPanel`), so each
 * app injects its own route and the component stays unaware of the integration.
 */
export const InspectionReportDialog = ({
  inspection,
  onOpenChange,
  fetchReport,
}: {
  // Null closes it; holding the inspection lets the title say which one it is.
  inspection: OrderWorkProofInspectionSummary | null;
  onOpenChange: (open: boolean) => void;
  fetchReport: (
    inspectionId: string,
  ) => Promise<FetchOrderWorkProofReportResponseBody>;
}) => {
  const t = useTranslations('orders.workProof');
  const tReport = useTranslations('orders.workProof.report');

  const inspectionId = inspection?.id;

  const query = useQuery({
    queryKey: buildInspectionReportQueryKey(inspectionId ?? ''),
    queryFn: () => fetchReport(inspectionId!),
    enabled: !!inspectionId,
    // The URL expires: it is asked for on open and never cached.
    staleTime: 0,
    cacheTime: 0,
    refetchInterval: (data) =>
      data?.status === 'generating' ? GENERATING_POLL_MS : false,
  });

  const retry = useCallback(() => query.refetch(), [query]);

  const status = query.data?.status;

  return (
    <Dialog open={!!inspection} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-5xl'>
        <DialogHeader>
          <DialogTitle>
            {inspection
              ? t('title', { id: inspection.external_id })
              : tReport('title')}
          </DialogTitle>
        </DialogHeader>

        {query.isError ? (
          // The endpoint itself is unreachable: this IS the only retry that can
          // do something, unlike a `failed` report, which the server governs.
          <div className='flex flex-col items-center gap-3'>
            <Message icon={alertIcon}>{tReport('loadError')}</Message>
            <Button size='sm' variant='outline' onClick={retry}>
              {tReport('retry')}
            </Button>
          </div>
        ) : query.isLoading || !status ? (
          <div className='flex min-h-[12rem] items-center justify-center'>
            <Spinner />
          </div>
        ) : status === 'ready' && query.data?.url ? (
          <div className='flex flex-col gap-4'>
            <div className='flex items-center justify-between'>
              <DialogDescription>{tReport('description')}</DialogDescription>
              <a
                href={query.data.url}
                target='_blank'
                rel='noreferrer'
                className='flex w-fit items-center gap-1.5 self-end text-[13px] hover:underline'
              >
                <IconExternalLink className='size-4' />
                {tReport('openInNewTab')}
              </a>
            </div>
            <IFrameLoader url={query.data.url} />
          </div>
        ) : (
          // No button on `failed`: the cooldown and the attempt cap live in the
          // server and only the insurer's "Actualizar" clears them.
          <Message icon={status === 'generating' ? <Spinner /> : alertIcon}>
            {tReport(`status.${status === 'ready' ? 'generating' : status}`)}
          </Message>
        )}
      </DialogContent>
    </Dialog>
  );
};
